import { AppState } from 'src/app/viewbid/state/app-state';
import { Store } from '@ngrx/store';
import { HttpStatusCode } from '@angular/common/http';
import {
    ConfirmationWindowComponent,
    TokenStorageService,
    User,
} from 'viewbid-shared-lib';
import { selectUser } from '../../state/app-selectors';
import {
    ApplicationPreferencesFormResponse,
    AuctionCatalogsService,
    BidderBid,
    GetWatcherByIdResponse,
    LotStatus,
    PostBidLotState,
    WatchedItemsFlattenedListResponse,
} from '@viewbid/ts-angular-client';
import { Injectable } from '@angular/core';
import { catchError, filter, map, shareReplay } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { BiddingErrorCodes } from '../../modules/profiles/enums/bidding-error-codes';
import { TranslateService } from '@ngx-translate/core';
import { VbSnackbarService } from '@viewbid/ui';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';

export function reconcileHighBidder(
    previousValue: string | null | undefined,
    currentUserValue: string,
    isHighBidder: boolean,
): string | null {
    if (isHighBidder) {
        return currentUserValue;
    }
    return previousValue === currentUserValue ? null : (previousValue ?? null);
}

export interface FieldOption {
    value: string;
    label: string;
}

export interface ArrayAttributeField {
    attributeKey: string;
    label: string;
    presentAs: string;
    mandatory: boolean;
    start: number | null;
    finish: number | null;
    listKey: string | null;
    options?: FieldOption[];
}

export interface FormField {
    categoryKey: string;
    categoryLabel: string;
    attributeGroup: string;
    fields: Fields[];
}

export interface Fields {
    attributeKey: string;
    label: string;
    presentAs: string;
    mandatory: boolean;
    start: number | null;
    finish: number | null;
    listKey: string | null;
    arrayAttributeFields?: ArrayAttributeField[];
}

export interface ImageFile {
    uploadId?: string;
    fileId?: string;
    filename: string;
    type: string;
    url?: string;
}

export interface ImageData {
    files: ImageFile[];
}

export interface InstructionData {
    PAGE: string;
    URL: string;
    IMAGE: ImageData;
}

export interface ApiResponse {
    description: string;
    formFields: FormField[];
    data: {
        PAGES: InstructionData[];
    };
    accessToken?: string;
}

const DO_NOT_ASK_BID_CONFIRMATION_AUCTIONS_KEY: string =
    'doNotAskBidConfirmationAuctions';
const KEY_HELP_LINKAGE: string = 'HELP_LINKAGE';

function toInstructions(response: ApiResponse): InstructionData[] {
    if (!Array.isArray(response?.data?.PAGES)) {
        return [];
    }
    return response.data.PAGES.map((item: InstructionData) => {
        const file: ImageFile = item.IMAGE.files[0];
        return {
            PAGE: item.PAGE,
            URL: item.URL,
            IMAGE: {
                files: [
                    {
                        uploadId: file.uploadId,
                        fileId: file.fileId,
                        filename: file.filename,
                        type: file.type,
                        url: file.url,
                    },
                ],
            },
        };
    });
}

@Injectable({
    providedIn: 'root',
})
export class BiddingUtils {
    private readonly detailsPageLosingIndicatorImagePath: string =
        '../../../../assets/img/icon/icon_loosing_hand.svg';
    private readonly detailsPageWinningIndicatorImagePath: string =
        '../../../../assets/img/icon/icon_winning_hand.svg';
    private readonly reserveIndicatorImagePath: string =
        '../../../../assets/img/icon/icon_winning_hand.svg';
    watchList: Array<GetWatcherByIdResponse> = [];
    userBids: Array<BidderBid> = [];
    private instructionsRequest$: Observable<InstructionData[]> | null = null;

    constructor(
        private store: Store<AppState>,
        private translateService: TranslateService,
        private readonly snackbarService: VbSnackbarService,
        private readonly auctionService: AuctionCatalogsService,
        private readonly tokenStorageService: TokenStorageService,
    ) {}

    /**
     * Retrieves the accountMappingsId from the user state.
     *
     * @returns {Observable<string>} An observable that emits the accountMappingsId.
     */
    public getCurrentUserId(): Observable<string> {
        return this.store.select<User | null>(selectUser).pipe(
            filter((user: User | null): user is User => user !== null),
            map((user: User) => user.accountMappingsId || ''),
        );
    }

    public getLotDetailsWinningIndicatorPath(
        isWinningBid: boolean,
        isLosingBid: boolean,
    ): string | null {
        return isWinningBid
            ? this.detailsPageWinningIndicatorImagePath
            : isLosingBid
              ? this.detailsPageLosingIndicatorImagePath
              : null;
    }

    public checkReserveAmountMet(
        currentBid: number,
        reserveAmount: number,
    ): boolean {
        return currentBid < reserveAmount;
    }

    public showToolTipText(
        isWinningBid: boolean,
        productStatus: LotStatus,
    ): string {
        if (isWinningBid) {
            return 'You already have the winning bid.';
        } else {
            switch (productStatus) {
                case LotStatus.Published:
                    return 'Place bid';
                case LotStatus.Complete:
                    return 'Bidding completed.';
                case LotStatus.NotSold:
                    return 'This item did not sell.';
                case LotStatus.Draft:
                    return '';
                default:
                    return '';
            }
        }
    }

    public applyLotStateToLotItem(
        lotItem: WatchedItemsFlattenedListResponse,
        lotState: PostBidLotState,
        currentUserId: string,
    ): void {
        lotItem.currentBid = lotState.currentBid;
        lotItem.minimumBidAmount = lotState.minimumNextBid;
        lotItem.lotToDatetime = lotState.toDatetime;
        lotItem.currentHighBidderId = reconcileHighBidder(
            lotItem.currentHighBidderId,
            currentUserId,
            lotState.isHighBidder,
        );
    }

    updateWatchlist(
        eventData: GetWatcherByIdResponse,
        watcherId: string,
    ): void {
        const existingItem: GetWatcherByIdResponse | undefined =
            this.watchList.find(
                (item: GetWatcherByIdResponse) => item.id === watcherId,
            );

        if (!existingItem) {
            this.watchList.push(eventData);
        }
    }

    removeFromWatchlist(auctionLotId: string): void {
        this.watchList = this.watchList.filter(
            (item: GetWatcherByIdResponse) =>
                item.auctionLotId !== auctionLotId,
        );
    }

    getErrorMessage(error: any): string {
        const errorMessages: Map<number, string> = new Map<number, string>([
            [BiddingErrorCodes.USER_IS_PENDING, 'Bidding.userIsPending'],
            [BiddingErrorCodes.WATCHER_NOT_FOUND, 'Bidding.watcherNotFound'],
            [BiddingErrorCodes.BID_NOT_FOUND, 'Bidding.proxyBidNotFound'],
            [
                BiddingErrorCodes.AUCTION_NOT_PUBLISHED,
                'Bidding.auctionNotPublished',
            ],
            [
                BiddingErrorCodes.BIDDING_DATA_FOUND,
                'Bidding.biddingDataNotFound',
            ],
            [
                BiddingErrorCodes.CURRENT_HIGH_BIDDER,
                'Bidding.currentHighestBidder',
            ],
            [
                BiddingErrorCodes.VALID_PROFILE_NOT_FOUND,
                'Bidding.invalidProfile',
            ],
            [BiddingErrorCodes.USER_IS_SUSPENDED, 'Bidding.userSuspended'],
            [BiddingErrorCodes.USER_IS_RESTRICTED, 'Bidding.userRestricted'],
            [
                BiddingErrorCodes.BID_CREATION_FAILED,
                'Bidding.bidCreationFailed',
            ],
            [
                BiddingErrorCodes.BID_COLLISION_OCCURRED,
                'Bidding.bidCollisionError',
            ],
            [BiddingErrorCodes.BID_UPDATE_FAILED, 'Bidding.bidUpdateFailed'],
        ]);

        const errorCode: number | undefined = error.error?.errorCode;
        if (errorCode && errorMessages.has(errorCode)) {
            return this.translateService.instant(errorMessages.get(errorCode)!);
        }

        return (
            error.error?.message ||
            this.translateService.instant('Bidding.biddingError')
        );
    }

    async showBidMaintenanceDialog(
        modalService: BsModalService,
        header: string,
        mainText: string,
        buttonLabel: string,
    ): Promise<void> {
        const modalRef: BsModalRef = modalService.show(
            ConfirmationWindowComponent,
            {
                class: 'modal-md custom-modal-width modal-dialog-centered',
            },
        );
        modalRef.content.headerText = header;
        modalRef.content.mainText = mainText;
        modalRef.content.buttonLabel = buttonLabel;
        modalRef.content.showCancelButton = false;
    }

    private addToLocalStorageArray(
        key: string,
        value: string,
        preventDuplicates: boolean = false,
    ): void {
        const existingArray: string[] = JSON.parse(
            window.localStorage.getItem(key) || '[]',
        );

        // Check for duplicates if needed
        if (preventDuplicates && existingArray.includes(value)) {
            return;
        }

        existingArray.push(value);
        window.localStorage.setItem(key, JSON.stringify(existingArray));
    }

    public shouldShowBidConfirmation(auctionId: string): boolean {
        const existingArray: string[] = JSON.parse(
            window.localStorage.getItem(
                DO_NOT_ASK_BID_CONFIRMATION_AUCTIONS_KEY,
            ) || '[]',
        );

        return !existingArray.includes(auctionId);
    }

    /**
     * Persists the user's "don't ask again" choice for an auction so future
     * bids on it skip the confirmation step. Used by callers that own their
     * own confirmation UI (e.g. the lot-details vb-bid-confirm-dialog) rather
     * than the built-in ngx-bootstrap dialog.
     */
    public setSkipBidConfirmation(auctionId: string): void {
        this.addToLocalStorageArray(
            DO_NOT_ASK_BID_CONFIRMATION_AUCTIONS_KEY,
            auctionId,
            true,
        );
    }

    get instructions$(): Observable<InstructionData[]> {
        this.instructionsRequest$ ??= this.fetchInstructions();
        return this.instructionsRequest$;
    }

    private fetchInstructions(): Observable<InstructionData[]> {
        return this.auctionService
            .getApplicationPreferenceKeyForm(KEY_HELP_LINKAGE)
            .pipe(
                map((res: ApplicationPreferencesFormResponse) =>
                    toInstructions(res as unknown as ApiResponse),
                ),
                catchError((error: any) => {
                    this.instructionsRequest$ = null;
                    if (!this.isSessionEnding(error)) {
                        this.snackbarService.error(
                            this.translateService.instant(
                                'biddingInstruction.fetchError',
                            ),
                        );
                    }
                    return of([]);
                }),
                shareReplay({ bufferSize: 1, refCount: false }),
            );
    }

    /** The interceptor is already logging the user out on these failures;
     *  a fetch-error toast would flash over the redirect to login. */
    private isSessionEnding(error: any): boolean {
        return (
            !this.tokenStorageService.getToken() ||
            error?.status === 0 ||
            error?.status === HttpStatusCode.Unauthorized ||
            error?.status === HttpStatusCode.Forbidden
        );
    }

    public getInstructionForCurrentPage$(
        currentUrl: string,
    ): Observable<InstructionData | null> {
        return this.instructions$.pipe(
            map((instructions: InstructionData[]) => {
                const urlPath: string = this.normalizeUrlPath(currentUrl);
                const sortedInstructions: InstructionData[] =
                    this.sortBySpecificity(instructions);
                return (
                    sortedInstructions.find((instruction: InstructionData) =>
                        this.matchesPattern(instruction, urlPath),
                    ) || null
                );
            }),
        );
    }

    private normalizeUrlPath(url: string): string {
        const pathname: string = new URL(url).pathname;
        // Strip matrix parameters (e.g., ;invoiceNo=5;id=xxx)
        return pathname.split(';')[0];
    }

    private sortBySpecificity(
        instructions: InstructionData[],
    ): InstructionData[] {
        return [...instructions].sort(
            (a: InstructionData, b: InstructionData) => {
                const aSegments: number = this.countPathSegments(a.PAGE);
                const bSegments: number = this.countPathSegments(b.PAGE);
                return bSegments - aSegments; // More segments = more specific
            },
        );
    }

    private countPathSegments(path: string): number {
        return path.split('/').filter((s: string) => s.length > 0).length;
    }

    private matchesPattern(
        instruction: InstructionData,
        urlPath: string,
    ): boolean {
        const pattern: string = this.buildRegexPattern(instruction.PAGE);

        // Try exact match first
        if (new RegExp(`^${pattern}$`).test(urlPath)) {
            return true;
        }

        // Allow flexible matching for simple patterns
        return this.allowsFlexibleMatch(instruction.PAGE, pattern, urlPath);
    }

    private buildRegexPattern(page: string): string {
        let pattern: string = page.replace(/{:id}/g, '[^/]+');

        // Trailing slash indicates implicit route parameter
        if (pattern.endsWith('/')) {
            pattern += '[^/]+';
        }

        return pattern;
    }

    private allowsFlexibleMatch(
        page: string,
        pattern: string,
        urlPath: string,
    ): boolean {
        // Only allow flexible matching if pattern doesn't have specific segments after {:id}
        const hasSpecificSegmentsAfter: boolean = page.includes('/{:id}/');
        const endsWithSlash: boolean = pattern.endsWith('/');

        if (!hasSpecificSegmentsAfter && !endsWithSlash) {
            return new RegExp(`^${pattern}(/.*)?$`).test(urlPath);
        }

        return false;
    }

    showMinimumBidChangedDialog(modalService: BsModalService): void {
        const modalRef: BsModalRef = modalService.show(
            ConfirmationWindowComponent,
            {
                class: 'modal-md custom-modal-width modal-dialog-centered',
                ignoreBackdropClick: true,
                keyboard: false,
                initialState: {
                    headerText: this.translateService.instant(
                        'Bidding.quickBidUpdateTitle',
                    ),
                    mainText: this.translateService.instant(
                        'Bidding.quickBidUpdateMessage',
                    ),
                    buttonLabel: this.translateService.instant(
                        'Bidding.quickBidUpdateButtonLabel',
                    ),
                    showCancelButton: false,
                    showClosePopupIcon: false,
                },
            },
        );
        modalRef.content.response.subscribe((response: string) => {
            if (response) {
                window.location.reload();
            }
        });
    }
}
